import {useEffect, useState} from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import {useParams} from 'react-router-dom'
import {ArtifactPreviewPanel} from '../components/ArtifactPreviewPanel'
import type {PanelArtifactVersion, SessionArtifact} from '../components/rosArtifacts'
import {config} from '../config/environment'
import {useRosFeatures} from '../access/useRosFeatures'
import {fetchArtifacts} from './artifactsApi'
import {flattenArtifacts, typeDef} from './artifactsModel'

/** Full-window artifact viewer used by the library's direct-open action. */
export function ArtifactViewPage() {
  const {artifactId = ''} = useParams()
  const {auth, authFetch, enabled} = useRosFeatures()
  const artifactsMgmtEnabled = enabled('artifacts-mgmt')
  const appArtifactsEnabled = enabled('app-artifacts')
  const [artifact, setArtifact] = useState<SessionArtifact | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetchArtifacts(config.API_CF_URL, auth.token, authFetch as typeof fetch)
      .then(groups => flattenArtifacts(groups, artifactsMgmtEnabled, appArtifactsEnabled))
      .then(items => { if (!cancelled) setArtifact(items.find(item => item.id === artifactId) ?? null) })
      .catch(() => { if (!cancelled) setArtifact(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [artifactId, auth.token, authFetch, artifactsMgmtEnabled, appArtifactsEnabled])

  const download = async (item: SessionArtifact, version: PanelArtifactVersion) => {
    const headers: Record<string, string> = {}
    if (auth.token) headers.Authorization = `Bearer ${auth.token}`
    const url = item.type === 'app-artifact'
      ? `${config.API_CF_URL}/api/r3/app-artifacts/${encodeURIComponent(item.id)}/versions/${version.version}/download`
      : version.bodyR2Key
        ? `${config.API_CF_URL}/api/r3/artifact-body?key=${encodeURIComponent(version.bodyR2Key)}`
        : null
    if (!url) return
    const response = await authFetch(url, {credentials: 'include', headers})
    if (!response.ok) return
    const blob = item.type === 'app-artifact'
      ? await response.blob()
      : new Blob([await response.text()], {type: 'application/json;charset=utf-8'})
    const objectUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = `${item.title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}-v${version.version}${typeDef(item.type).ext}`
    anchor.click()
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  }

  if (loading) {
    return <Box sx={{height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><CircularProgress /></Box>
  }
  if (!artifact) {
    return <Box sx={{height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><Typography>Artifact not found.</Typography></Box>
  }
  return (
    <Box sx={{height: '100vh', overflow: 'hidden', bgcolor: 'background.paper'}}>
      <ArtifactPreviewPanel
        artifacts={[artifact]}
        apiBaseUrl={config.API_CF_URL}
        token={auth.token}
        onDownload={download}
        onClose={() => window.close()}
      />
    </Box>
  )
}
